import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { errorMessage, unwrap } from 'api/call'
import {
  getHomeBackofficeNumbers,
  type BackofficeNumbersResponse,
} from 'api/generated'

interface HomeState {
  users: number
  activeUsers: number
  activeUsers1m: number
  activeUsers1y: number
  usersCreatedLastMonth: number
  updateTasksRunning: string
  membershipCodesNotConsumed: string
  reportsPending: string
  membershipCodes24h: number
  membershipCodes1w: number
  membershipCodes1m: number
  membershipCodes1wAgo: number
  membershipCodes1mAgo: number
  messagesLast24h: number
  messagesLast1w: number
  visitEventsLast24h: string
  visitEventsLast1w: string
  newUsersLast24h: number
  newUsersLast1w: number
  /** App features (workouts, trainings, friendly matches, recurring trainings): total, 24 h, 7 days */
  activityTrends: BackofficeNumbersResponse['data']['activity_trends'] | null
  /** Match Finder: findable users, open matches, matches created and matched */
  matchFinder: BackofficeNumbersResponse['data']['match_finder'] | null
  loading: boolean
  error: string | null
}

const initialState: HomeState = {
  users: 0,
  activeUsers: 0,
  activeUsers1m: 0,
  activeUsers1y: 0,
  usersCreatedLastMonth: 0,
  updateTasksRunning: '0',
  membershipCodesNotConsumed: '0',
  reportsPending: '0',
  membershipCodes24h: 0,
  membershipCodes1w: 0,
  membershipCodes1m: 0,
  membershipCodes1wAgo: 0,
  membershipCodes1mAgo: 0,
  messagesLast24h: 0,
  messagesLast1w: 0,
  visitEventsLast24h: '0',
  visitEventsLast1w: '0',
  newUsersLast24h: 0,
  newUsersLast1w: 0,
  activityTrends: null,
  matchFinder: null,
  loading: false,
  error: null,
}

export const getHomeData = createAsyncThunk<
  BackofficeNumbersResponse,
  void,
  { rejectValue: string }
>('home/getHomeData', async (_, { rejectWithValue }) => {
  try {
    return await unwrap(getHomeBackofficeNumbers())
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to fetch home data'))
  }
})

// Home slice
const homeSlice = createSlice({
  name: 'home',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getHomeData.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getHomeData.fulfilled, (state, action) => {
        state.loading = false
        state.users = action.payload.numbers.users
        state.activeUsers = action.payload.numbers.active_users_24h
        state.activeUsers1m = action.payload.numbers.active_users_1m
        state.activeUsers1y = action.payload.numbers.active_users_1y
        state.usersCreatedLastMonth =
          action.payload.numbers.users_created_last_month
        state.updateTasksRunning = action.payload.data.update_tasks.toString()
        state.membershipCodesNotConsumed =
          action.payload.data.membership_codes.toString()
        state.reportsPending = action.payload.data.reports.toString()
        state.membershipCodes24h = action.payload.data.membership_codes_24h
        state.membershipCodes1w = action.payload.data.membership_codes_1w
        state.membershipCodes1m = action.payload.data.membership_codes_1m
        state.membershipCodes1wAgo = action.payload.data.membership_codes_1w_ago
        state.membershipCodes1mAgo = action.payload.data.membership_codes_1m_ago
        state.messagesLast24h = action.payload.data.messages_last_24h
        state.messagesLast1w = action.payload.data.messages_last_1w
        state.visitEventsLast24h = action.payload.data.visit_events_last_24h
        state.visitEventsLast1w = action.payload.data.visit_events_last_1w
        state.newUsersLast24h = action.payload.data.new_users_last_24h
        state.newUsersLast1w = action.payload.data.new_users_last_1w
        state.activityTrends = action.payload.data.activity_trends
        state.matchFinder = action.payload.data.match_finder
      })
      .addCase(getHomeData.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload ?? 'Failed to fetch home data'
      })
  },
})

export const homeSliceReducer = homeSlice.reducer
